/*
 * © 2020 Microsoft. All rights reserved.
 */

define('Solitaire/MvvmStructure/Service/SolitaireDailyChallengeService',[
        'lodash',
        'pixi',
        'absolute/storagemanager',
        'Managers/ChallengeManager',
        'Managers/ChallengeFactory',
        'Solitaire/MvvmStructure/Service/ManifestService',
        'Managers/StatManager',
        'Managers/TelemetryManager'],
    function (_, PIXI, StorageManager, ChallengeManager, ChallengeFactory, ManifestService, StatManager, TelemetryManager) {
        "use strict";

        var SolitaireDailyChallengeService = {

            _storageKey: "dailychallenges",

            _challengeCache: null,

            _dayCache: null,

            _isValid: true,

            _defaultStats: {
                playedChallenges: 0,
                completedChallenges: 0,
                totalChallenges: 0,
                coinsEarned: 0,
                bonusEarned: 0,
                totalCoins: 0,
                daysCompleted: 0,
                bronzeBadgesWon: 0,
                silverBadgesWon: 0,
                goldBadgesWon: 0,
                diamondBadgesWon: 0,
                perfectCompletions: 0,
                klondikeChallenges: 0,
                spiderChallenges: 0,
                freecellChallenges: 0,
                pyramidChallenges: 0,
                tripeaksChallenges: 0,
                easyChallenges: 0,
                mediumChallenges: 0,
                hardChallenges: 0,
                expertChallenges: 0
            },

            Initialize: function (onSuccess, onError) {
                this.GetChallengesForCurrentMonth(
                    onSuccess,
                    onError);
            },

            IsValid: function () {
                return ManifestService.IsValid();
            },

            HasCache: function () {
                if (!this._challengeCache) {
                    this._challengeCache = StorageManager.fetch(this._storageKey);
                }
                return !!this._challengeCache;
            },

            BuildMonthYearString: function (date, previousMonth = false) {
                var month = previousMonth ? date.getMonth() : date.getMonth() + 1,
                    monthStr = month < 10 ? "0" + month : "" + month,
                    year = date.getFullYear(),
                    yearStr = year.toString().substring(2);
                return monthStr + yearStr;
            },

            LoadJsonFromCDN: function (url, onSuccess, onError) {
                var loader = new PIXI.JsonLoader(url, true /* cross origin */);

                function onLoaded() {
                    loader.removeEventListener('loaded', onLoaded);
                    loader.removeEventListener('error', onError);
                    onSuccess(loader.json);
                }

                function onFailed(error) {
                    loader.removeEventListener('loaded', onLoaded);
                    loader.removeEventListener('error', onError);
                    onError();
                }

                loader.addEventListener('loaded', onLoaded);
                loader.addEventListener('error', onFailed);

                loader.load();
            },


            LoadDataFromCDN: function (key, onSuccess, onError) {

                var settingsData = ManifestService.GetContentCategoryEntry("dailychallenges", "all", key);

                if (!settingsData || !settingsData.name) {
                    onError();
                }
                else {
                    this.LoadJsonFromCDN(settingsData.name, onSuccess, onError);
                }
            },

            GetDataForMonth: function (key, onSuccess, onError) {

                var data = null;

                // do we already have in memory?
                if (this._challengeCache && this._challengeCache[key]) {
                    data = this._challengeCache[key];
                }
                else {

                    // do we already have in local storage?
                    this._challengeCache = StorageManager.fetch(this._storageKey);

                    if (this._challengeCache) {
                        data = this._challengeCache[key];
                    }
                }


                if (this._challengeCache) {
                    let monthYearStr = this.BuildMonthYearString(new Date());
                    var monthsChallenges = this._challengeCache["monthchallenges" + monthYearStr];
                    var challengeSettings = this._challengeCache["monthchallengesettings" + monthYearStr];

                    if (monthsChallenges && challengeSettings) {
                        var shouldBreak = false;
                        _.forEach(monthsChallenges.DailyChallenges, function (dateChallenges) {
                            _.forEach(dateChallenges.Challenges, function (challengeConfig) {
                                var ci = ChallengeManager.GetChallengeConfig(challengeConfig.Key);
                                if (!ci) {
                                    data = null;
                                    shouldBreak = true;
                                    return false;
                                }
                            });
                            if (shouldBreak) return false;
                        });
                    }
                }

                // send it back
                if (data) {
                    setTimeout(function () {
                        onSuccess(data)
                    }, 0);
                }
                else {
                    // get it from the CDN
                    this.LoadDataFromCDN(
                        key,
                        function (response) {

                            if (!this._challengeCache) {
                                this.dataNotExists = true;
                                this._challengeCache = {};
                            }

                            // save in memory cache
                            this._challengeCache[key] = response;

                            // save in local storage
                            StorageManager.store(this._storageKey, this._challengeCache);

                            onSuccess(response);

                        }.bind(this),
                        function () {
                            this._isValid = false;
                            onError()
                        }.bind(this));
                }
            },

            GetChallengesData: function () {
                var playerProgressData = {};
                if (this._challengeCache) {
                    for (var key in this._challengeCache) {
                        if (this._challengeCache.hasOwnProperty(key) && key.indexOf('settings') === -1) {
                            var datePair = key.split('monthchallenges')[1], month = datePair.substr(0, 2),
                                year = datePair.substr(2, 2);
                            playerProgressData[year] = playerProgressData[year] || {};
                            playerProgressData[year][month] =
                                this.GetChallengeStatsForMonth(month.toString() + year.toString());
                        }
                    }
                } else {
                    var date = new Date();
                    var month = date.getMonth() + 1,
                        monthStr = month < 10 ? "0" + month : "" + month,
                        year = date.getFullYear(),
                        yearStr = year.toString().substring(2);
                    playerProgressData[yearStr] = {};
                    playerProgressData[yearStr][monthStr] = _.extend({}, this._defaultStats);
                }
                return playerProgressData;
            },

            GetChallengesForCurrentMonth: function (onSuccess, onError) {
                this.GetDataForMonth("monthchallengesettings" + this.BuildMonthYearString(new Date()),
                    function () {
                        this.GetDataForMonth("monthchallenges" + this.BuildMonthYearString(new Date()),
                            function (response) {
                                this._dayCache = {};
                                this.AddToDayCache(response);
                                onSuccess(response);
                            }.bind(this), onError);
                    }.bind(this), onError);
            },

            SaveCurrentChallenges: function () {
                StorageManager.store(this._storageKey, this._challengeCache);
            },

            AddToDayCache: function (challengeData) {
                _.forEach(challengeData.DailyChallenges, function (dateChallenges) {

                    var dateStr = dateChallenges.Date; // formatted like "/Date(1429228800000)/"

                    if (dateStr) {
                        var timestampStr = dateStr.substring(6, 19);
                        //console.log(timestampStr);
                        var date = new Date(parseInt(timestampStr));
                        //console.log(date.toUTCString());

                        var day = date.getUTCDate(),
                            month = date.getUTCMonth() + 1,
                            year = date.getUTCFullYear(),
                            indexStr = (day < 10 ? "0" + day : day) + (month < 10 ? "0" + month : month) + year;

                        this._dayCache[indexStr] = dateChallenges;

                        // store all the challenges for this day in the challenge manager
                        _.forEach(dateChallenges.Challenges, function (challenge) {
                            ChallengeManager.StoreChallenge(challenge);
                        });

                    }
                }, this);

                // save everything to local storage
                ChallengeManager.Sync();

            },

            GetChallengeSettingsForCurrentMonth: function (onSuccess, onError) {
                this.GetChallengeSettingsForMonth(this.BuildMonthYearString(new Date()), onSuccess, onError);
            },

            GetChallengeSettingsForMonth: function (monthYearStr, onSuccess, onError) {
                var key = "monthchallengesettings" + monthYearStr;

                if (!(this._challengeCache && this._challengeCache[key])) {
                    this.GetChallengesForCurrentMonth(
                        function () {
                            onSuccess(this._challengeCache[key]);
                        }.bind(this),
                        function () {
                            onError();
                        });
                }
                else {
                    onSuccess(this._challengeCache[key])
                }
            },


            GetChallengeStatsForCurrentMonth: function () {
                return this.GetChallengeStatsForMonth(this.BuildMonthYearString(new Date()));
            },

            GetChallengeStatsForMonth: function (monthYearStr) {
                var stats = _.extend({}, this._defaultStats);
                var currentMonthYearString = this.BuildMonthYearString(new Date());
                // challenges must already be cached!
                var monthsChallenges = this._challengeCache["monthchallenges" + monthYearStr];
                var challengeSettings = this._challengeCache["monthchallengesettings" + monthYearStr];

                if (monthsChallenges && challengeSettings) {
                    _.forEach(monthsChallenges.DailyChallenges, function (dateChallenges) {
                        var dateCompleted = true;
                        _.forEach(dateChallenges.Challenges, function (challengeConfig) {
                            if (!challengeConfig) {
                                return;
                            }
                            var ci = ChallengeManager.GetChallengeConfig(challengeConfig.Key);

                            if (!ci) {
                                TelemetryManager.challengeIsNull(
                                    monthYearStr,
                                    challengeConfig.AdditionalDataNeeded,
                                    challengeConfig.Coins,
                                    challengeConfig.Completed,
                                    challengeConfig.Date,
                                    challengeConfig.Difficulty,
                                    challengeConfig.Key
                                );
                            }

                            // TODO: this should unblock users, but need to investigate root cause of error of ci being undefined/null
                            if (ci) {
                                stats.totalChallenges++;
                                stats.totalCoins += ci.Coins;
                                if (ci.Played) {
                                    stats.playedChallenges++;
                                }
                                if (ci.Completed) {
                                    stats.completedChallenges++;
                                    stats.coinsEarned += ci.Coins;
                                    if (monthYearStr === currentMonthYearString) {
                                        switch (ci.Data.GameMode) {
                                            case 'Klondike':
                                                stats.klondikeChallenges++;
                                                break;
                                            case 'Spider':
                                                stats.spiderChallenges++;
                                                break;
                                            case 'FreeCell':
                                                stats.freecellChallenges++;
                                                break;
                                            case 'Pyramid':
                                                stats.pyramidChallenges++;
                                                break;
                                            case 'TriPeaks':
                                                stats.tripeaksChallenges++;
                                                break;
                                        }

                                        switch (ci.Difficulty) {
                                            case 1:
                                                stats.easyChallenges++;
                                                break;
                                            case 2:
                                                stats.mediumChallenges++;
                                                break;
                                            case 3:
                                                stats.hardChallenges++;
                                                break;
                                            case 4:
                                                stats.expertChallenges++;
                                                break;
                                        }

                                    } else {
                                        stats.klondikeChallenges = 0;
                                        stats.spiderChallenges = 0;
                                        stats.freecellChallenges = 0;
                                        stats.pyramidChallenges = 0;
                                        stats.tripeaksChallenges = 0;
                                        stats.easyChallenges = 0;
                                        stats.mediumChallenges = 0;
                                        stats.hardChallenges = 0;
                                        stats.expertChallenges = 0;
                                    }
                                } else {
                                    dateCompleted = false;
                                }
                            }
                        });

                        if (dateCompleted) {
                            stats.daysCompleted++;
                            stats.coinsEarned += dateChallenges.DayBonusCoins;
                        }

                        stats.totalCoins += dateChallenges.DayBonusCoins;
                    });

                    if (stats.coinsEarned >= challengeSettings.Badges[0].CoinsNeeded) {
                        stats.bronzeBadgesWon++;

                        if (stats.coinsEarned >= challengeSettings.Badges[1].CoinsNeeded) {
                            stats.silverBadgesWon++;

                            if (stats.coinsEarned >= challengeSettings.Badges[2].CoinsNeeded) {
                                stats.goldBadgesWon++;

                                if (stats.coinsEarned >= challengeSettings.DiamondBadge.CoinsNeeded) {
                                    stats.diamondBadgesWon++;
                                }
                            }
                        }
                    }
                }
                return stats;

            },

            UpdateGlobalChallengeStats: function () {
                if (!this._challengeCache) {
                    return;
                }
                var globalStats = this.GetGlobalChallengeStats();
                StatManager.SetStatistic("DCGlobalPlayedChallenges", globalStats.playedChallenges);
                StatManager.SetStatistic("DCGlobalCompletedChallenges", globalStats.completedChallenges);
                StatManager.SetStatistic("DCGlobalDaysCompleted", globalStats.daysCompleted);
                StatManager.SetStatistic("DCGlobalBronzeBadgesWon", globalStats.bronzeBadgesWon);
                StatManager.SetStatistic("DCGlobalSilverBadgesWon", globalStats.silverBadgesWon);
                StatManager.SetStatistic("DCGlobalGoldBadgesWon", globalStats.goldBadgesWon);
                StatManager.SetStatistic("DCGlobalDiamondBadgesWon", globalStats.diamondBadgesWon);
                StatManager.SetStatistic("DCGlobalPerfectCompletions", globalStats.perfectCompletions);

                StatManager.Save();
            },

            GetGlobalChallengeStats: function () {
                var globalStats = _.extend({}, this._defaultStats);

                var stats = _.extend({}, this._defaultStats);

                if (this._challengeCache) {
                    _.forEach(this._challengeCache, function (cacheEntry, key) {
                        if (key.indexOf("monthchallenges") == 0) {
                            stats = this.GetChallengeStatsForMonth(key.substring("monthschallenges".length - 1));
                            globalStats.playedChallenges += stats.playedChallenges;
                            globalStats.completedChallenges += stats.completedChallenges;
                            globalStats.totalChallenges += stats.totalChallenges;
                            globalStats.coinsEarned += stats.coinsEarned;
                            globalStats.totalCoins += stats.totalCoins;
                            globalStats.daysCompleted += stats.daysCompleted;
                            globalStats.bronzeBadgesWon += stats.bronzeBadgesWon;
                            globalStats.silverBadgesWon += stats.silverBadgesWon;
                            globalStats.goldBadgesWon += stats.goldBadgesWon;
                            globalStats.diamondBadgesWon += stats.diamondBadgesWon;
                            globalStats.klondikeChallenges += stats.klondikeChallenges;
                            globalStats.spiderChallenges += stats.spiderChallenges;
                            globalStats.freecellChallenges += stats.freecellChallenges;
                            globalStats.pyramidChallenges += stats.pyramidChallenges;
                            globalStats.tripeaksChallenges += stats.tripeaksChallenges;
                            globalStats.easyChallenges += stats.easyChallenges;
                            globalStats.mediumChallenges += stats.mediumChallenges;
                            globalStats.hardChallenges += stats.hardChallenges;
                            globalStats.expertChallenges += stats.expertChallenges;
                            globalStats.perfectCompletions += (stats.coinsEarned === stats.totalCoins ? 1 : 0);
                        }
                    }, this);
                }
                return globalStats;
            },

            GetDateIndex: function (date) {
                var day = date.getDate(),
                    month = date.getMonth() + 1,
                    year = date.getFullYear(),
                    indexStr = (day < 10 ? "0" + day : day) + (month < 10 ? "0" + month : month) + year;
                return indexStr;
            },

            GetCompletedChallengeCountForDate: function (date) {

                var indexStr = this.GetDateIndex(date),
                    challengesData = this._dayCache[indexStr];

                var completed = 0;
                if (challengesData) {
                    _.forEach(challengesData.Challenges, function (challengeConfig) {

                        var ci = ChallengeManager.GetChallengeConfig(challengeConfig.Key);

                        if (ci && ci.Completed) {
                            completed++;
                        }
                    });
                }

                return completed;
            },

            CreateDailyChallenges: function (date) {
                var day = date.getDate(),
                    month = date.getMonth() + 1,
                    year = date.getFullYear(),
                    indexStr = (day < 10 ? "0" + day : day) + (month < 10 ? "0" + month : month) + year;

                // look up the challenge data, create and pass back challenge objects
                var challengesData = this._dayCache[indexStr];

                var challenge = null, challenges = [];

                if (challengesData) {
                    _.forEach(challengesData.Challenges, function (challengeConfig) {

                        challenge = ChallengeFactory.BuildChallengeObject(ChallengeManager.GetChallengeConfig(challengeConfig.Key));
                        challenge.dailyBonus = challengesData.DayBonusCoins;
                        if (challenge) {
                            challenges.push(challenge);
                        }

                    }, this);
                }

                return challenges;
            },

            cleanLocalStorage: function () {
                var dailychallenges = StorageManager.fetch(this._storageKey);

                if (dailychallenges) {
                    var currentMonthDate = this.BuildMonthYearString(new Date());
                    var previousMonthDate = this.BuildMonthYearString(new Date(), true);

                    //do not delete badges information
                    for (var key in dailychallenges) {
                        if ((!key.includes(currentMonthDate) && !key.includes(previousMonthDate)) &&
                            !key.includes('monthchallengesettings')) {
                            delete dailychallenges[key];
                        }
                    }

                    StorageManager.store(this._storageKey, dailychallenges);
                }

                this._challengeCache = null;
            }
        };

        return SolitaireDailyChallengeService;
    });

